import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { opacityInOut } from '../../animations/opacity-in-out.animation';
import { scaleInOut } from '../../animations/scale-in-out.animation';
import { HelperService } from 'src/app/core/services/helper.service';

@Component({
  selector: 'addon-quantity-selector-modal',
  templateUrl: './addon-quantity-selector-modal.component.html',
  styleUrls: ['./addon-quantity-selector-modal.component.scss'],
  animations: [opacityInOut, scaleInOut],
})
export class AddonQuantitySelectorModalComponent implements OnInit, OnDestroy {
  @Input() title = '';
  @Input() description = '';
  @Input() minQuantity = 1;
  @Input() maxQuantity: number | null = null;
  @Input() defaultQuantity = 1;

  @Output() onChanged = new EventEmitter<number | null>();

  quantityControl: FormControl<number | null>;

  constructor(
    private translate: TranslateService,
    private helperService: HelperService
  ) {}

  ngOnInit(): void {
    this.helperService.blockScroll$.next(true);
    this.quantityControl = new FormControl<number | null>(
      this.defaultQuantity,
      { validators: this.buildValidators() }
    );
  }

  buildValidators(): ValidatorFn[] {
    const validators: ValidatorFn[] = [
      Validators.required,
      Validators.min(this.minQuantity),
    ];

    if (this.maxQuantity != null) {
      validators.push(Validators.max(this.maxQuantity));
    }

    return validators;
  }

  close(): void {
    this.onChanged.emit(null);
  }

  confirm(): void {
    if (this.quantityControl.invalid) {
      this.quantityControl.markAllAsTouched();
      return;
    }
    this.onChanged.emit(this.quantityControl.value);
  }

  get showErrors(): boolean {
    return this.quantityControl.touched || this.quantityControl.dirty;
  }

  getErrorMessage(): string {
    const control = this.quantityControl;

    if (!control || !this.showErrors || !control.errors) {
      return '';
    }

    const errors = control.errors as ValidationErrors;

    if (errors['required']) {
      return this.translate.instant('AddOns.Errors.QuantityRequired');
    }

    if (errors['min']) {
      return this.translate.instant('AddOns.Errors.QuantityMin', {
        min: errors['min'].min,
      });
    }

    if (errors['max']) {
      return this.translate.instant('AddOns.Errors.QuantityMax', {
        max: errors['max'].max,
      });
    }

    return '';
  }

  ngOnDestroy(): void {
    this.helperService.blockScroll$.next(false);
  }
}
